import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Field } from "./Fleet";
import { validateRut, formatRut } from "@/lib/rut";
import { Plus, X, Trash2 } from "lucide-react";

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/drivers").then((r) => setDrivers(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="drivers-page">
      <PageHeader
        tag="Recursos"
        title="Conductores"
        description="Gestiona el registro de conductores. Validación automática de RUT chileno (Módulo 11)."
        action={
          <button onClick={() => setShowForm(true)} className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 flex items-center gap-2" data-testid="new-driver-btn">
            <Plus size={18} strokeWidth={2.5} /> Nuevo Conductor
          </button>
        }
      />

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <Th>Nombre</Th>
              <Th>RUT</Th>
              <Th>Licencia</Th>
              <Th>Teléfono</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((d) => (
              <tr key={d.id} className="border-b border-gray-200" data-testid={`driver-row-${d.rut}`}>
                <td className="px-4 py-4 text-sm font-bold">{d.name}</td>
                <td className="px-4 py-4 text-sm font-mono">{d.rut}</td>
                <td className="px-4 py-4 text-sm font-mono">{d.license_number || "-"}</td>
                <td className="px-4 py-4 text-sm font-mono">{d.phone || "-"}</td>
                <td className="px-4 py-4 text-right">
                  <button onClick={async () => { await api.delete(`/drivers/${d.id}`); load(); }} className="text-red-600 hover:bg-red-50 p-2">
                    <Trash2 size={16} strokeWidth={2.5} />
                  </button>
                </td>
              </tr>
            ))}
            {drivers.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">Sin conductores.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && <NewDriverModal onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}

function NewDriverModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", rut: "", license_number: "", phone: "", email: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validateRut(form.rut)) {
      setError("RUT inválido. Ejemplo válido: 12.345.678-5");
      return;
    }
    setLoading(true);
    try {
      await api.post("/drivers", { ...form, rut: formatRut(form.rut) });
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-display text-2xl">Nuevo Conductor</h2>
          <button onClick={onClose}><X size={24} strokeWidth={2.5} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5" data-testid="new-driver-form">
          <Field label="Nombre completo" required value={form.name} onChange={update("name")} testId="driver-name" />
          <Field label="RUT" required value={form.rut} onChange={update("rut")} mono placeholder="12.345.678-5" testId="driver-rut" />
          <Field label="N° Licencia" value={form.license_number} onChange={update("license_number")} mono testId="driver-license" />
          <Field label="Teléfono" value={form.phone} onChange={update("phone")} mono testId="driver-phone" />
          <Field label="Correo" type="email" value={form.email} onChange={update("email")} mono testId="driver-email" />
          {error && <div className="status-danger px-4 py-3 text-sm" data-testid="driver-error">{error}</div>}
          <button type="submit" disabled={loading} className="w-full h-touch bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60" data-testid="save-driver-btn">
            {loading ? "Guardando..." : "Guardar Conductor"}
          </button>
        </form>
      </div>
    </div>
  );
}
